/**
 * PostHog in automations: a pull step that reads how visitors use one page
 * before an agent step redesigns it, and a trigger that starts a workflow
 * when one reading on a page crosses a line. Both name the page by the
 * address PostHog records it under.
 */

export interface PostHogPullStep {
  id: string
  type: 'pull'
  provider: 'posthog'
  /** the page, e.g. https://example.com/pricing — query and fragment are ignored */
  url: string
  canvasId: string
}

export const POSTHOG_METRICS = ['bounce', 'exit', 'unread', 'rage', 'dead'] as const
export type PostHogMetric = (typeof POSTHOG_METRICS)[number]

/** What each reading measures: a share of visits in percent, or a count of
 *  events, over the trigger's window. */
export const POSTHOG_METRIC_UNIT: Record<PostHogMetric, 'pct' | 'count'> = {
  bounce: 'pct',
  exit: 'pct',
  unread: 'pct',
  rage: 'count',
  dead: 'count',
}

export const POSTHOG_METRIC_LABEL: Record<PostHogMetric, string> = {
  bounce: 'bounce rate',
  exit: 'exit rate',
  unread: 'visits leaving before half the page',
  rage: 'rage clicks',
  dead: 'dead clicks',
}

export interface PostHogWatchTrigger {
  type: 'posthog'
  url: string
  metric: PostHogMetric
  /** fires when the reading is above this */
  threshold: number
  /** the window the reading covers, in days */
  days: number
}

export const POSTHOG_WATCH_DAYS = [1, 7, 14, 30] as const
export const DEFAULT_POSTHOG_WATCH: Omit<PostHogWatchTrigger, 'url'> = {
  type: 'posthog',
  metric: 'bounce',
  threshold: 50,
  days: 7,
}

/** How often a watch reads PostHog. A personal key has a few hundred queries
 *  an hour; a reading is six of them. */
export const POSTHOG_WATCH_INTERVAL_MS = 15 * 60_000

export function isPostHogMetric(value: unknown): value is PostHogMetric {
  return typeof value === 'string' && (POSTHOG_METRICS as readonly string[]).includes(value)
}

/** A cheap shape check for the editor: the server resolves the address. */
export function looksLikePageUrl(url: string): boolean {
  try {
    const parsed = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`)
    return parsed.hostname.includes('.')
  } catch {
    return false
  }
}

export function formatPostHogReading(metric: PostHogMetric, value: number): string {
  return POSTHOG_METRIC_UNIT[metric] === 'pct' ? `${value}%` : String(value)
}

/** "bounce rate on /pricing passes 55%" — the trigger as list rows say it */
export function describePostHogWatch(trigger: PostHogWatchTrigger): string {
  return `${POSTHOG_METRIC_LABEL[trigger.metric]} on ${pagePath(trigger.url)} passes ${formatPostHogReading(trigger.metric, trigger.threshold)}`
}

/** The page's path for a short label: "/pricing", or the host for a home page. */
export function pagePath(url: string): string {
  try {
    const parsed = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`)
    const path = parsed.pathname.replace(/\/+$/, '')
    return path || parsed.host
  } catch {
    return url
  }
}
